import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Circle, Maximize2, Minimize2, RotateCw } from 'lucide-react';
import { type ProxyConnectionState, useHttpProxySession } from '../hooks/useHttpProxySession';
import { useWebCapture } from '../hooks/useWebCapture';
import { dismissRequest, isRequestDismissed, useCaptureRequest } from '../hooks/useCaptureRequest';
import WebCaptureConsent from './WebCaptureConsent';
import { type CaptureReason, disclosureFor } from '@/lib/webCaptureDisclosure';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface DeviceWebProxyProps {
  deviceId: string;
  /**
   * Offer to record this session for support. Set when the device could
   * not be identified — those are the ones we need help with, and the ones
   * where the offer is worth the interruption.
   */
  offerCapture?: boolean;
}

const STATE_LABELS: Record<ProxyConnectionState, string> = {
  idle: 'Not connected',
  connecting: 'Connecting…',
  connected: 'Connected',
  error: 'Connection error',
  disconnected: 'Disconnected',
};

const STATE_STYLE: Record<ProxyConnectionState, string> = {
  idle: 'bg-muted text-muted-foreground',
  connecting: 'bg-sev-5/15 text-sev-5',
  connected: 'bg-sev-4/15 text-sev-4',
  error: 'bg-sev-1/15 text-sev-1',
  disconnected: 'bg-muted text-muted-foreground',
};

export default function DeviceWebProxy({ deviceId, offerCapture = false }: DeviceWebProxyProps) {
  const { sessionId, connectionState, proxyUrl, open, close } =
    useHttpProxySession(deviceId);
  const capture = useWebCapture(deviceId);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [currentPath] = useState('/');
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  /**
   * Device UIs are built for a whole browser window, not a panel inside one.
   * A fixed height crops them on both axes with no way out — the frame cannot
   * be told to scroll from here, because whether it scrolls at all is the
   * device page's decision, not ours. So give the frame room instead: it
   * defaults to most of the viewport, drags taller, and expands to fill the
   * window for the ones that need the width too.
   */
  const [expanded, setExpanded] = useState(false);
  /**
   * Recording is available on every device, always — not only where we have
   * decided to ask for it. Someone who can see their switch misbehaving
   * should be able to show us without waiting to be prompted, and on a device
   * we already support there is no prompt coming.
   *
   * `offerCapture` therefore no longer controls whether recording is
   * *possible*, only whether we volunteer the ask.
   */
  const [userRequested, setUserRequested] = useState(false);
  const [note, setNote] = useState('');

  // Something we have asked to be shown on this device. The server only ever
  // returns one that is still outstanding — retired and already-answered asks
  // never reach here — so the only judgement left to the client is whether
  // this person waved it away.
  const { data: requestData } = useCaptureRequest(deviceId);
  const pendingRequest = requestData?.request ?? null;
  const askOffered =
    pendingRequest !== null && !isRequestDismissed(pendingRequest.id) && !dismissed;

  const reason: CaptureReason = userRequested
    ? 'userInitiated'
    : askOffered
      ? 'requested'
      : 'unidentified';

  /**
   * Consent, then reconnect with recording armed.
   *
   * The reconnect is the whole point: recording has to be live before the
   * first request so the login exchange is captured. Starting it on the
   * open session would miss the login, which is the most useful page in
   * the capture.
   */
  const handleStartCapture = useCallback(async () => {
    setBusy(true);

    // A targeted ask records where the customer is standing. Only the
    // profile-authoring cases need the login exchange, and only they are worth
    // a re-login to get it.
    if (reason === 'requested' && sessionId) {
      try {
        const consentId = await capture.grantConsent(
          disclosureFor('requested', pendingRequest?.ask),
          note,
          pendingRequest?.id,
        );
        await capture.startInPlace(sessionId, consentId);
      } catch (err) {
        console.error('Failed to start web capture:', err);
      } finally {
        setBusy(false);
      }
      return;
    }

    // Only reopen if we actually closed. Recording consent is recorded
    // *before* the session is torn down, so a failure there leaves the
    // original session alive and untouched — reopening regardless would
    // orphan it on the gateway and strand a session on a device that
    // usually permits only one.
    let closed = false;
    try {
      const consentId = await capture.grantConsent(disclosureFor(reason), note);
      await close();
      closed = true;
      await open(consentId);
      capture.markRecording();
    } catch (err) {
      console.error('Failed to start web capture:', err);
      if (closed) {
        // We tore the session down and failed to bring the recording one
        // up. Put the user back on a plain browsing session rather than
        // leaving them staring at nothing.
        try {
          await open();
        } catch {
          /* surfaced by connectionState */
        }
      }
    } finally {
      setBusy(false);
    }
  }, [capture, close, open, reason, note, sessionId, pendingRequest]);

  const handleStopCapture = useCallback(async () => {
    if (!sessionId) return;
    setBusy(true);
    try {
      await capture.stop(sessionId);
    } finally {
      setBusy(false);
    }
  }, [capture, sessionId]);

  const handleDecision = useCallback(
    async (decide: () => Promise<void>) => {
      setBusy(true);
      try {
        await decide();
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  // Read by the cleanup below when it runs, not when it was set up: the
  // recording usually starts after the session it records.
  const recordingRef = useRef(false);
  const stopRef = useRef(capture.stop);
  useEffect(() => {
    recordingRef.current = capture.stage === 'recording';
    stopRef.current = capture.stop;
  });

  useEffect(() => {
    return () => {
      if (sessionId) {
        // Leaving with a recording running: stop it first, so it goes to the
        // gateway's review store instead of being thrown away with the
        // session. Best effort — nothing here can show the review.
        const stopped = recordingRef.current ? stopRef.current(sessionId) : Promise.resolve(true);
        void stopped.finally(() => close());
      }
    };
  }, [sessionId, close]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data === '__proxy_frameset_reload__' && iframeRef.current) {
        const src = iframeRef.current.src;
        iframeRef.current.src = '';
        iframeRef.current.src = src;
      }
    };
    window.addEventListener('message', handler);
    return () => { window.removeEventListener('message', handler); };
  }, []);

  const handleConnect = useCallback(async () => {
    try {
      await open();
    } catch (err) {
      console.error('Failed to open web proxy session:', err);
    }
  }, [open]);

  /**
   * A recording lives in the session until it is stopped, and closing the
   * session throws it away. Stopping moves it to the gateway's review store,
   * which outlives the session, so a disconnect mid-recording stops first and
   * lands in review. If the stop fails, stay connected rather than lose it.
   */
  const handleDisconnect = useCallback(async () => {
    setBusy(true);
    try {
      if (capture.stage === 'recording' && sessionId && !(await capture.stop(sessionId))) {
        return;
      }
      await close();
    } finally {
      setBusy(false);
    }
  }, [capture, sessionId, close]);

  // Expanded covers the whole window, so Escape has to get out of it — there
  // is nothing else on screen to click.
  useEffect(() => {
    if (!expanded) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => { window.removeEventListener('keydown', onKeyDown); };
  }, [expanded]);

  const handleRefresh = useCallback(() => {
    if (iframeRef.current && connectionState === 'connected') {
      const src = iframeRef.current.src;
      iframeRef.current.src = '';
      iframeRef.current.src = src;
    }
  }, [connectionState]);

  const iframeSrc = connectionState === 'connected' ? proxyUrl() : undefined;

  return (
    <div
      className={cn(
        'rounded-md border border-border bg-card p-3',
        // Expanded is a class change on the same element, never a different
        // branch of the tree: re-mounting the iframe would reload the device
        // page and throw away wherever the user had navigated to.
        expanded && 'fixed inset-0 z-50 flex flex-col overflow-auto rounded-none',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Badge className={cn('border-0 font-normal', STATE_STYLE[connectionState])}>
            {STATE_LABELS[connectionState]}
          </Badge>
          {connectionState === 'connected' ? (
            <span className="text-xs text-muted-foreground font-mono">{currentPath}</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {connectionState === 'connected' ? (
            <>
              {capture.stage === 'off' && !userRequested ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setUserRequested(true); setDismissed(false); }}
                  className="gap-1.5"
                  title="Record this session to show us something"
                >
                  <Circle className="h-3 w-3 fill-sev-1 text-sev-1" />
                  Record
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="gap-1.5"
                title="Refresh"
              >
                <RotateCw className="w-3.5 h-3.5" />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setExpanded(!expanded); }}
                className="gap-1.5"
                title={expanded ? 'Exit full window (Esc)' : 'Fill the window'}
              >
                {expanded ? (
                  <>
                    <Minimize2 className="w-3.5 h-3.5" />
                    Exit
                  </>
                ) : (
                  <>
                    <Maximize2 className="w-3.5 h-3.5" />
                    Expand
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-sev-1 hover:text-sev-1"
                disabled={busy}
                onClick={() => { void handleDisconnect(); }}
              >
                Disconnect
              </Button>
            </>
          ) : null}
          {(connectionState === 'idle' ||
            connectionState === 'disconnected' ||
            connectionState === 'error') ? (
            <Button onClick={() => { void handleConnect(); }}>
              Connect to web UI
            </Button>
          ) : null}
        </div>
      </div>

      {/*
        Shown when we are volunteering the ask, when the user asked for it, or
        whenever a capture is already under way — the last case matters most:
        once recording starts the panel is the only way to stop and review it,
        so it must not depend on why it began. A recording in review outlives
        the session (Disconnect stops it first), so review stays on screen.
      */}
      {(connectionState === 'connected' || capture.stage === 'review' || capture.stage === 'done') &&
      (capture.stage !== 'off' || askOffered || ((offerCapture || userRequested) && !dismissed)) ? (
        <WebCaptureConsent
          stage={capture.stage}
          reason={reason}
          ask={pendingRequest?.ask ?? null}
          summary={capture.summary}
          error={capture.error}
          busy={busy}
          note={note}
          onNoteChange={setNote}
          onStart={() => { void handleStartCapture(); }}
          onStop={() => { void handleStopCapture(); }}
          onApprove={() => { void handleDecision(capture.approve); }}
          onDiscard={() => { void handleDecision(() => capture.discard()); }}
          onDismiss={() => {
            // Waving away an ask has to outlive the component, or "Not now"
            // is a lie the next page load exposes.
            if (askOffered) dismissRequest(pendingRequest.id);
            setDismissed(true);
            setUserRequested(false);
            setNote('');
            capture.reset();
          }}
        />
      ) : null}

      {/*
        Deliberately not sandboxed, and the backend no longer assumes it is.
        The IE-era UIs this exists to reach are framesets whose frames drive
        each other (`parent.LeftTop.location.href`) and touch document.cookie;
        a sandbox gives every nested frame its own opaque origin, so both throw
        and the device UI stops working. The proxy route is authenticated
        instead — see `proxy_request` in website-backend.

        The consequence is that device HTML runs on our origin. Containing that
        needs a separate origin for the proxy, not a sandbox attribute.
      */}
      {connectionState === 'connected' && iframeSrc ? (
        // The wrapper carries the size, so the drag handle belongs to it
        // rather than the frame: `resize` needs a non-visible overflow, and
        // browsers do not put a handle on a replaced element like <iframe>.
        //
        // `pb-3` is load-bearing. Under border-box the padding comes out of
        // the wrapper's height, so the iframe's `h-full` stops short of the
        // bottom edge and the handle gets a strip of its own. Sitting over the
        // frame instead, the drag dies the moment the pointer crosses into it
        // — the iframe swallows the move events.
        <div
          className={cn(
            'w-full overflow-hidden rounded-md border border-border-subtle bg-card pb-3',
            expanded ? 'min-h-0 flex-1' : 'h-[70vh] min-h-96 resize-y',
          )}
        >
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            className="h-full w-full bg-white"
            title={`Device Web UI - ${deviceId}`}
          />
        </div>
      ) : null}

      {connectionState === 'connecting' ? (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">
            Establishing connection to device web UI…
          </p>
        </div>
      ) : null}

      {connectionState === 'idle' ? (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">
            Click &quot;Connect to web UI&quot; to access the device&apos;s web-based
            configuration interface.
          </p>
        </div>
      ) : null}

      {connectionState === 'error' ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to connect to the device&apos;s web UI. The device may not have an
            HTTP connection method configured, or the gateway may be offline.
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
